import { Box, Button, Group, Paper, Skeleton, Stack, Text } from "@mantine/core";
import type { AufgabeDringlichkeit, AufgabeQuelle } from "@msc/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import { useAufgaben, useTodoErledigen } from "../../hooks/useAufgaben.js";
import { ApiErrorAlert } from "../ApiErrorAlert.js";
import { AufgabeZeile } from "./AufgabeZeile.js";

type Filter = "alle" | AufgabeQuelle;

const FILTER: Filter[] = ["alle", "todo", "besuch", "systemstand", "statusbericht", "freigabe", "datenluecke"];

const ABSCHNITTE: AufgabeDringlichkeit[] = ["ueberfaellig", "woche", "spaeter"];

/** Punkt vor der Abschnittsüberschrift: Orange markiert Überfälliges, Navy und Grau den Rest. */
const PUNKT: Record<AufgabeDringlichkeit, string> = {
  ueberfaellig: "var(--mantine-color-orange-6)",
  woche: "var(--msc-navy)",
  spaeter: "var(--msc-linie-stark)",
};

/**
 * „Meine Aufgaben": Filter je Quelle, drei Abschnitte nach Dringlichkeit (die
 * Reihenfolge darin liefert die API), darunter der eingeklappte Hinweis auf
 * Punkte, die niemand im Tool erledigen kann.
 */
export function AufgabenListe({ heute = new Date() }: { heute?: Date }) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  // Zwei Grenzen aus router.ts: /inbox ohne, /datenluecken mit Geschäftsleitung.
  const darfInbox = hasRole(user, ["admin", "bereichsleiter", "stellvertreter"]);
  const darfDatenluecken = hasRole(user, ["admin", "geschaeftsleitung", "bereichsleiter", "stellvertreter"]);
  const { data, isLoading, error } = useAufgaben();
  const erledigen = useTodoErledigen();
  const [filter, setFilter] = useState<Filter>("alle");

  if (error) return <ApiErrorAlert error={error} />;
  if (isLoading || !data) return <Skeleton h={240} radius="md" />;

  const sichtbar = data.items.filter((i) => filter === "alle" || i.quelle === filter);
  const anzahl = (f: Filter) => (f === "alle" ? data.items.length : data.items.filter((i) => i.quelle === f).length);
  const { anzahl: nichtZuordenbar, beispiele } = data.nichtZuordenbar;

  return (
    <Stack gap="lg">
      <Group gap="xs" role="group" aria-label={t("aufgaben.filter.label")}>
        {FILTER.map((f) => (
          <Button
            key={f}
            radius="xl"
            size="sm"
            variant={filter === f ? "filled" : "default"}
            color={filter === f ? "blue" : undefined}
            aria-pressed={filter === f}
            onClick={() => setFilter(f)}
            rightSection={
              <Text span size="sm" opacity={0.75}>
                {anzahl(f)}
              </Text>
            }
          >
            {t(`aufgaben.filter.${f}`)}
          </Button>
        ))}
      </Group>

      {ABSCHNITTE.map((d) => {
        const items = sichtbar.filter((i) => i.dringlichkeit === d);
        const label = t(`aufgaben.abschnitt.${d}`);
        return (
          <Paper key={d} component="section" aria-label={label} withBorder radius="md" style={{ overflow: "hidden" }}>
            <Group justify="space-between" px="md" py="sm">
              <Group gap="xs">
                <Box w={8} h={8} style={{ borderRadius: 999, background: PUNKT[d] }} aria-hidden />
                <Text
                  fw={600}
                  size="sm"
                  style={{ color: d === "ueberfaellig" ? "var(--msc-dringend)" : undefined }}
                >
                  {label}
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {items.length}
              </Text>
            </Group>
            {items.length === 0 ? (
              <Text size="sm" c="dimmed" px="md" pb="md">
                {t("aufgaben.leer")}
              </Text>
            ) : (
              items.map((a) => (
                <AufgabeZeile
                  key={a.id}
                  aufgabe={a}
                  ueberfaellig={d === "ueberfaellig"}
                  heute={heute}
                  darfInbox={darfInbox}
                  onErledigen={(todoId) => erledigen.mutate(todoId)}
                />
              ))
            )}
          </Paper>
        );
      })}

      {nichtZuordenbar > 0 ? (
        <Paper
          component="details"
          withBorder
          radius="md"
          px="md"
          py="sm"
          style={{ borderStyle: "dashed" }}
        >
          <Text component="summary" size="sm" fw={500} style={{ cursor: "pointer" }}>
            {t("aufgaben.nichtZuordenbar.titel", { anzahl: nichtZuordenbar })}
          </Text>
          <Text size="sm" mt="xs">
            {[
              t("aufgaben.nichtZuordenbar.text", { count: nichtZuordenbar }),
              beispiele.length > 0
                ? t("aufgaben.nichtZuordenbar.beispiele", { namen: beispiele.join(", ") })
                : null,
              darfDatenluecken ? t("aufgaben.nichtZuordenbar.wo") : null,
            ]
              .filter(Boolean)
              .join(" ")}
          </Text>
        </Paper>
      ) : null}
    </Stack>
  );
}
